import { useEffect } from 'react';

/**
 * Hook personnalisé useSEO pour mettre à jour dynamiquement
 * les balises Meta, le Titre, les balises Open Graph et les Données Structurées JSON-LD de chaque page.
 *
 * @param {Object} options
 * @param {string} options.title - Titre de la page
 * @param {string} options.description - Meta description de la page
 * @param {string} options.keywords - Mots-clés pertinents pour la page
 * @param {string} options.canonicalUrl - URL canonique (ex: https://gsulespalmiers.bj/filieres)
 * @param {string} options.ogImage - URL de l'image de partage Open Graph
 * @param {string} options.ogType - Type Open Graph (website, article)
 * @param {Object} options.schemaJson - Données structurées Schema.org JSON-LD optionnelles
 */
export const useSEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = 'https://gsulespalmiers.bj/images/logo-gsu.png',
  ogType = 'website',
  schemaJson = null
} = {}) => {
  useEffect(() => {
    // 1. Titre du document
    const defaultTitle = 'Groupe Scolaire et Universitaire Les Palmiers | Togba, Abomey-Calavi';
    const finalTitle = title 
      ? `${title} | GSU Les Palmiers` 
      : defaultTitle;
    document.title = finalTitle;

    // Helper pour mettre à jour ou créer une balise meta
    const setMetaTag = (selector, attributeName, attributeValue, contentValue) => {
      if (!contentValue) return;
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', contentValue);
    };

    // 2. Meta description
    const defaultDesc = "Le Groupe Scolaire et Universitaire Les Palmiers à Togba (Abomey-Calavi, Bénin) propose un enseignement d'excellence : CPEG, CPET, CPFPA et ISP. 1er Lycée d'Abomey-Calavi.";
    setMetaTag('meta[name="description"]', 'name', 'description', description || defaultDesc);

    // 3. Meta keywords
    if (keywords) {
      setMetaTag('meta[name="keywords"]', 'name', 'keywords', keywords);
    }

    // 4. Balise Canonique
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const finalUrl = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : 'https://gsulespalmiers.bj/');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', finalUrl);

    // 5. Open Graph tags
    setMetaTag('meta[property="og:title"]', 'property', 'og:title', finalTitle);
    setMetaTag('meta[property="og:description"]', 'property', 'og:description', description || defaultDesc);
    setMetaTag('meta[property="og:url"]', 'property', 'og:url', finalUrl);
    setMetaTag('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMetaTag('meta[property="og:type"]', 'property', 'og:type', ogType);

    // 6. Twitter Card tags
    setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', finalTitle);
    setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', description || defaultDesc);
    setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    // 7. Données Structurées JSON-LD spécifiques à la page (si fournies)
    let pageSchemaScript = document.getElementById('page-schema-jsonld');
    if (schemaJson) {
      if (!pageSchemaScript) {
        pageSchemaScript = document.createElement('script');
        pageSchemaScript.id = 'page-schema-jsonld';
        pageSchemaScript.type = 'application/ld+json';
        document.head.appendChild(pageSchemaScript);
      }
      pageSchemaScript.textContent = JSON.stringify(schemaJson);
    } else if (pageSchemaScript) {
      pageSchemaScript.remove();
    }

    // Scroll to top fluide à chaque changement de page
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, schemaJson]);
};

export default useSEO;
